import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { visitsAPI, clientsAPI, geofenceAPI, caregiverProfilesAPI } from '../services/api';
import { isOnline as isNetOnline, queueAction } from '../services/offlineQueue';
import { fmtClientTimeOnly, fmtClientTime, tzAbbrev, utcIsoToClientTzInput, clientTzInputToUtcIso } from '../services/timezone';
import VisitCompletionDialog from '../components/VisitCompletionDialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Clock, Play, Stop, MapPin, User, Calendar, Timer, CheckCircle, Warning,
  NavigationArrow, Spinner, ShieldWarning
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const ClockPage = () => {
  const { user } = useAuth();
  const [visits, setVisits] = useState([]);
  const [clients, setClients] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [now, setNow] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [activeVisit, setActiveVisit] = useState(null);
  const [elapsedTime, setElapsedTime] = useState('00:00:00');
  const [viewGps, setViewGps] = useState(null);
  const [geofenceAlerts, setGeofenceAlerts] = useState([]);
  const [alertFilter, setAlertFilter] = useState('false');
  const [completionOpen, setCompletionOpen] = useState(false);
  const [completionVisit, setCompletionVisit] = useState(null);
  const [submittingCheckout, setSubmittingCheckout] = useState(false);
  // Supervisor force-clock-out state
  const [forceVisit, setForceVisit] = useState(null);
  const [forceReason, setForceReason] = useState('');
  const [forceTime, setForceTime] = useState('');  // datetime-local in client TZ
  const [forceSaving, setForceSaving] = useState(false);

  const today = format(new Date(), 'yyyy-MM-dd');

  useEffect(() => { loadData(); }, []);

  // Timer for active visit
  useEffect(() => {
    let interval;
    if (activeVisit?.check_in_time) {
      interval = setInterval(() => {
        const start = new Date(activeVisit.check_in_time);
        const now = new Date();
        const diff = Math.floor((now - start) / 1000);
        const hrs = String(Math.floor(diff / 3600)).padStart(2, '0');
        const mins = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
        const secs = String(diff % 60).padStart(2, '0');
        setElapsedTime(`${hrs}:${mins}:${secs}`);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [activeVisit]);

  // Global 1s tick so supervisor "currently clocked in" panel updates in real time
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const loadData = async () => {
    try {
      const [visitsRes, clientsRes, cgRes] = await Promise.all([
        visitsAPI.getAll({}),
        clientsAPI.getAll(),
        // Caregiver name lookup for the supervisor "currently clocked in" panel.
        // Cheap call; harmless for caregivers (returns 403, caught below).
        user?.role === 'supervisor' || user?.role === 'super_admin'
          ? caregiverProfilesAPI.getAll().catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
      ]);
      setVisits(visitsRes.data);
      setClients(clientsRes.data);
      setCaregivers(cgRes.data || []);
      // For caregivers, surface their own in-progress visit in the banner.
      // Supervisors do not get a banner — they see the full list panel instead.
      const isSup = user?.role === 'supervisor' || user?.role === 'super_admin';
      const ownActive = !isSup ? visitsRes.data.find(v => v.status === 'in_progress') : null;
      if (ownActive) setActiveVisit(ownActive);
      // Load geofence alerts for supervisors
      try {
        const alertsRes = await geofenceAPI.getAlerts({ is_resolved: 'false' });
        setGeofenceAlerts(alertsRes.data);
      } catch (e) { /* caregiver role won't have access */ }
    } catch (error) {
      console.error('Error loading visits:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGPSLocation = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        toast.error('Geolocation is not supported by your browser');
        resolve({ latitude: null, longitude: null, address: null });
        return;
      }
      setGpsLoading(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          // Try reverse geocoding
          let address = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
          try {
            const resp = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18`);
            const data = await resp.json();
            if (data.display_name) {
              address = data.display_name;
            }
          } catch (e) { /* use coords as fallback */ }
          setGpsLoading(false);
          resolve({ latitude: lat, longitude: lng, address });
        },
        (error) => {
          setGpsLoading(false);
          console.warn('GPS error:', error.message);
          toast.error('Could not get location. Please enable location services.');
          resolve({ latitude: null, longitude: null, address: null });
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    });
  };

  const handleCheckIn = async (visit) => {
    const gps = await getGPSLocation();
    // Pre-flight offline check — go straight to queue
    if (!isNetOnline()) {
      await queueAction('check-in', visit.id, gps);
      const optimisticTime = new Date().toISOString();
      toast.info('Offline — clock-in queued, will sync when online');
      setActiveVisit({
        ...visit,
        status: 'in_progress',
        check_in_time: optimisticTime,
        check_in_address: gps?.address,
        _pending_sync: true,
      });
      return;
    }
    try {
      const res = await visitsAPI.checkIn(visit.id, gps);
      if (res.data.geofence_alert) {
        toast.warning(`Geofence Alert: You are ${res.data.geofence_alert.distance_miles} miles from the client's location`, { duration: 8000 });
      } else {
        toast.success('Clocked in successfully');
      }
      setActiveVisit({ ...visit, ...res.data, status: 'in_progress' });
      loadData();
    } catch (error) {
      // Network error → enqueue. Real 4xx errors → surface to user.
      if (!error.response) {
        await queueAction('check-in', visit.id, gps);
        const optimisticTime = new Date().toISOString();
        toast.info('Network error — clock-in queued, will sync when online');
        setActiveVisit({
          ...visit,
          status: 'in_progress',
          check_in_time: optimisticTime,
          check_in_address: gps?.address,
          _pending_sync: true,
        });
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    }
  };

  const handleCheckOut = (visit) => {
    // Open the completion dialog — actual clock-out happens on dialog submit
    setCompletionVisit(visit);
    setCompletionOpen(true);
  };

  const handleSubmitCompletion = async ({ tasks_completed, caregiver_signature, client_signature, completion_notes }) => {
    if (!completionVisit) return;
    setSubmittingCheckout(true);
    const gps = await getGPSLocation();
    const payload = {
      tasks_completed,
      caregiver_signature,
      client_signature,
      completion_notes: completion_notes || null,
      latitude: gps?.latitude || null,
      longitude: gps?.longitude || null,
      address: gps?.address || null,
    };

    // Offline → queue immediately
    if (!isNetOnline()) {
      try {
        await queueAction('check-out', completionVisit.id, payload);
        toast.info('Offline — clock-out queued, will sync when online');
        setCompletionOpen(false);
        setCompletionVisit(null);
        setActiveVisit(null);
        setElapsedTime('00:00:00');
      } finally { setSubmittingCheckout(false); }
      return;
    }

    try {
      const res = await visitsAPI.checkOut(completionVisit.id, payload);
      if (res.data.geofence_alert) {
        toast.warning(`Geofence Alert: You are ${res.data.geofence_alert.distance_miles} miles from the client's location`, { duration: 8000 });
      } else {
        toast.success(`Clocked out — ${res.data.tasks_completed_count}/${res.data.tasks_total} tasks completed`);
      }
      setCompletionOpen(false);
      setCompletionVisit(null);
      setActiveVisit(null);
      setElapsedTime('00:00:00');
      loadData();
    } catch (error) {
      if (!error.response) {
        // Network failure → enqueue
        await queueAction('check-out', completionVisit.id, payload);
        toast.info('Network error — clock-out queued, will sync when online');
        setCompletionOpen(false);
        setCompletionVisit(null);
        setActiveVisit(null);
        setElapsedTime('00:00:00');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to clock out');
      }
    } finally { setSubmittingCheckout(false); }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      await geofenceAPI.resolveAlert(alertId);
      toast.success('Alert resolved');
      setGeofenceAlerts(geofenceAlerts.filter(a => a.id !== alertId));
    } catch (error) { toast.error('Failed to resolve alert'); }
  };

  const loadAlerts = async (filter) => {
    setAlertFilter(filter);
    try {
      const res = await geofenceAPI.getAlerts({ is_resolved: filter === 'all' ? undefined : filter });
      setGeofenceAlerts(res.data);
    } catch (e) {}
  };

  const getClientName = (clientId) => clients.find(c => c.id === clientId)?.name || 'Unknown Client';
  const getClientAddress = (clientId) => clients.find(c => c.id === clientId)?.address || '';
  const getClientTz = (clientId) => clients.find(c => c.id === clientId)?.timezone || 'America/New_York';
  const getCaregiverName = (caregiverId) =>
    caregivers.find(cg => cg.user_id === caregiverId)?.name || 'Unknown Caregiver';
  const formatElapsed = (startIso, nowDate) => {
    if (!startIso) return '00:00:00';
    const start = new Date(startIso);
    const diff = Math.max(0, Math.floor((nowDate - start) / 1000));
    const hrs = String(Math.floor(diff / 3600)).padStart(2, '0');
    const mins = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
    const secs = String(diff % 60).padStart(2, '0');
    return `${hrs}:${mins}:${secs}`;
  };

  const openForceCheckout = (visit) => {
    setForceVisit(visit);
    // Default suggested clock-out = right now in the client's TZ
    const tz = getClientTz(visit.client_id);
    setForceTime(utcIsoToClientTzInput(new Date().toISOString(), tz));
    setForceReason('');
  };

  const submitForceCheckout = async () => {
    if (!forceVisit) return;
    if (!forceReason.trim()) { toast.error('Please enter a reason'); return; }
    if (!forceTime) { toast.error('Please choose a clock-out time'); return; }
    const tz = getClientTz(forceVisit.client_id);
    const checkOutIso = clientTzInputToUtcIso(forceTime, tz);
    setForceSaving(true);
    try {
      await visitsAPI.supervisorCheckOut(forceVisit.id, {
        check_out_time: checkOutIso,
        reason: forceReason.trim(),
      });
      toast.success(`${getCaregiverName(forceVisit.caregiver_id)} clocked out`);
      setForceVisit(null);
      setForceReason('');
      setForceTime('');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clock out');
    } finally { setForceSaving(false); }
  };

  const todayVisits = visits.filter(v => v.scheduled_date === today);
  const scheduledVisits = todayVisits.filter(v => v.status === 'scheduled');
  const completedVisits = todayVisits.filter(v => v.status === 'completed');
  // ALL in-progress visits agency-wide (sorted by check-in time, newest first)
  const activeNowVisits = visits
    .filter(v => v.status === 'in_progress')
    .sort((a, b) => new Date(b.check_in_time || 0) - new Date(a.check_in_time || 0));

  // For supervisor view — show all visits with GPS data
  const isSupervisor = user?.role === 'supervisor' || user?.role === 'super_admin';
  const allVisitsWithGps = visits.filter(v => v.check_in_lat || v.check_out_lat);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;

  return (
    <div className="space-y-6 fade-in" data-testid="clock-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-semibold">
          {isSupervisor ? 'Clock In/Out Tracker' : 'My Clock In/Out'}
        </h1>
        <p className="text-muted-foreground mt-1">
          {isSupervisor ? 'Track caregiver clock in/out with GPS locations' : `Today: ${format(new Date(), 'EEEE, MMMM d, yyyy')}`}
        </p>
      </div>

      {/* Active Visit Banner */}
      {activeVisit && (
        <Card className="rounded-2xl border-2 border-green-500 bg-green-50/50" data-testid="active-visit-banner">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center animate-pulse">
                  <Timer weight="duotone" className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-green-700">Currently Clocked In</p>
                  <h2 className="font-heading text-2xl font-semibold">{getClientName(activeVisit.client_id)}</h2>
                  <p className="text-sm text-muted-foreground">Since {activeVisit.check_in_time ? `${fmtClientTimeOnly(activeVisit.check_in_time, getClientTz(activeVisit.client_id))} ${tzAbbrev(activeVisit.check_in_time, getClientTz(activeVisit.client_id))}` : ''}</p>
                  {(() => {
                    const addr = getClientAddress(activeVisit.client_id) || activeVisit.check_in_address;
                    return addr ? (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary flex items-center gap-1 mt-1 hover:underline touch-manipulation"
                        data-testid="active-visit-navigate"
                      >
                        <MapPin weight="duotone" className="w-3 h-3" />
                        <span className="truncate">{addr}</span>
                        <NavigationArrow weight="bold" className="w-3 h-3 shrink-0" />
                      </a>
                    ) : null;
                  })()}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="font-heading text-4xl font-bold text-green-700 font-mono" data-testid="elapsed-timer">{elapsedTime}</p>
                  <p className="text-xs text-muted-foreground">Elapsed Time</p>
                </div>
                <Button
                  size="lg"
                  className="rounded-2xl bg-red-600 hover:bg-red-700 text-white gap-2 px-8 h-14"
                  onClick={() => handleCheckOut(activeVisit)}
                  disabled={gpsLoading}
                  data-testid="clock-out-btn"
                >
                  {gpsLoading ? <Spinner className="w-5 h-5 animate-spin" /> : <Stop weight="bold" className="w-5 h-5" />}
                  Clock Out
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Supervisor: ALL caregivers currently clocked in (live elapsed) */}
      {isSupervisor && (
        <Card className="rounded-2xl border-2 border-green-200 bg-green-50/40" data-testid="active-clockins-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-600"></span>
              </span>
              Caregivers Currently Clocked In
              <Badge className="ml-1 rounded-full bg-green-100 text-green-800 text-xs" data-testid="active-clockins-count">
                {activeNowVisits.length}
              </Badge>
            </CardTitle>
            <CardDescription>Live view of every caregiver on a shift right now. Updated every second.</CardDescription>
          </CardHeader>
          <CardContent>
            {activeNowVisits.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Clock weight="duotone" className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No caregivers are clocked in right now.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="active-clockins-list">
                {activeNowVisits.map(v => {
                  const addr = getClientAddress(v.client_id) || v.check_in_address;
                  return (
                    <div
                      key={v.id}
                      className="p-4 rounded-xl bg-white border border-green-200 hover:border-green-300 transition-colors"
                      data-testid={`active-clockin-${v.id}`}
                    >
                      <div
                        className="flex items-start justify-between gap-3 cursor-pointer"
                        onClick={() => setViewGps(v)}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                              <User weight="duotone" className="w-5 h-5 text-green-700" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">{getCaregiverName(v.caregiver_id)}</p>
                              <p className="text-xs text-muted-foreground truncate">at {getClientName(v.client_id)}</p>
                            </div>
                          </div>
                          {addr && (
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`}
                              target="_blank" rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-xs text-primary hover:underline mt-2 inline-flex items-center gap-1"
                            >
                              <MapPin weight="duotone" className="w-3 h-3" /> <span className="truncate max-w-[200px]">{addr}</span>
                            </a>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            Since {v.check_in_time ? `${fmtClientTimeOnly(v.check_in_time, getClientTz(v.client_id))} ${tzAbbrev(v.check_in_time, getClientTz(v.client_id))}` : '—'}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-heading text-xl font-bold text-green-700 font-mono" data-testid={`active-elapsed-${v.id}`}>
                            {formatElapsed(v.check_in_time, now)}
                          </p>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Elapsed</p>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-green-100 flex gap-2">
                        <Button
                          size="sm"
                          className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 text-white gap-2"
                          onClick={(e) => { e.stopPropagation(); openForceCheckout(v); }}
                          data-testid={`force-checkout-btn-${v.id}`}
                        >
                          <Stop weight="bold" className="w-4 h-4" /> Clock Out
                        </Button>
                        <Button
                          size="sm" variant="outline" className="rounded-xl gap-1"
                          onClick={(e) => { e.stopPropagation(); setViewGps(v); }}
                          data-testid={`view-gps-btn-${v.id}`}
                        >
                          <MapPin weight="bold" className="w-4 h-4" /> Details
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Today's Summary */}
      {!isSupervisor && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="rounded-2xl border border-border">
            <CardContent className="p-6 text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Upcoming</p>
              <h3 className="font-heading text-3xl font-semibold mt-2">{scheduledVisits.length}</h3>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border border-border">
            <CardContent className="p-6 text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">In Progress</p>
              <h3 className="font-heading text-3xl font-semibold mt-2 text-green-600">{activeVisit ? 1 : 0}</h3>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border border-border">
            <CardContent className="p-6 text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Completed</p>
              <h3 className="font-heading text-3xl font-semibold mt-2">{completedVisits.length}</h3>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Scheduled Visits — Clock In */}
      {scheduledVisits.length > 0 && !activeVisit && (
        <Card className="rounded-2xl border border-border" data-testid="scheduled-visits-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Ready to Clock In</CardTitle>
            <CardDescription>Today's scheduled visits</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {scheduledVisits.map(visit => (
              <div key={visit.id} className="flex items-center justify-between p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors" data-testid={`scheduled-visit-${visit.id}`}>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Clock weight="duotone" className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{getClientName(visit.client_id)}</p>
                    <p className="text-sm text-muted-foreground">{visit.scheduled_time} &middot; {visit.duration_minutes} min</p>
                    {getClientAddress(visit.client_id) && (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(getClientAddress(visit.client_id))}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary flex items-center gap-1 mt-1 hover:underline touch-manipulation"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`navigate-${visit.id}`}
                      >
                        <MapPin weight="duotone" className="w-3 h-3" />
                        <span className="truncate">{getClientAddress(visit.client_id)}</span>
                        <NavigationArrow weight="bold" className="w-3 h-3 shrink-0" />
                      </a>
                    )}
                  </div>
                </div>
                <Button
                  size="lg"
                  className="rounded-2xl bg-green-600 hover:bg-green-700 text-white gap-2 px-6"
                  onClick={() => handleCheckIn(visit)}
                  disabled={gpsLoading}
                  data-testid={`clock-in-btn-${visit.id}`}
                >
                  {gpsLoading ? <Spinner className="w-4 h-4 animate-spin" /> : <Play weight="bold" className="w-4 h-4" />}
                  Clock In
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Completed Visits Today */}
      {completedVisits.length > 0 && (
        <Card className="rounded-2xl border border-border" data-testid="completed-visits-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Completed Today</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {completedVisits.map(visit => (
              <div key={visit.id} className="p-4 rounded-xl bg-muted/30" data-testid={`completed-visit-${visit.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                      <CheckCircle weight="duotone" className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{getClientName(visit.client_id)}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span>In: {visit.check_in_time ? fmtClientTimeOnly(visit.check_in_time, getClientTz(visit.client_id)) : '-'}</span>
                        <span>Out: {visit.check_out_time ? fmtClientTimeOnly(visit.check_out_time, getClientTz(visit.client_id)) : '-'}</span>
                        {visit.check_in_time && visit.check_out_time && (
                          <span className="font-medium text-foreground">
                            {((new Date(visit.check_out_time) - new Date(visit.check_in_time)) / 3600000).toFixed(1)} hrs
                          </span>
                        )}
                      </div>
                      {(visit.check_in_lat || visit.check_out_lat) && (
                        <Button variant="ghost" size="sm" className="rounded-xl gap-1 h-6 text-xs mt-1 p-0 px-2" onClick={() => setViewGps(visit)}>
                          <NavigationArrow weight="duotone" className="w-3 h-3" /> View GPS
                        </Button>
                      )}
                    </div>
                  </div>
                  <Badge className="bg-green-100 text-green-800 rounded-full">Completed</Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Supervisor: Geofence Alerts */}
      {isSupervisor && (
        <Card className="rounded-2xl border border-border" data-testid="geofence-alerts-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <ShieldWarning weight="duotone" className="w-5 h-5 text-destructive" />
                  Geofence Alerts
                </CardTitle>
                <CardDescription>Visits where caregiver location was outside the client's geofence</CardDescription>
              </div>
              <Select value={alertFilter} onValueChange={loadAlerts}>
                <SelectTrigger className="w-40 rounded-xl" data-testid="alert-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="false">Unresolved</SelectItem>
                  <SelectItem value="true">Resolved</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {geofenceAlerts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle weight="duotone" className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No geofence alerts</p>
              </div>
            ) : (
              <div className="space-y-3">
                {geofenceAlerts.map(alert => (
                  <div key={alert.id} className="p-4 rounded-xl bg-red-50/50 border border-red-200 hover:border-red-300 transition-colors" data-testid={`geofence-alert-${alert.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
                          <Warning weight="duotone" className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm text-red-800">
                            {alert.user_name} - {alert.event_type === 'check_in' ? 'Clock In' : 'Clock Out'}
                          </p>
                          <p className="text-sm text-muted-foreground">Client: {alert.client_name}</p>
                          <p className="text-xs text-red-700 font-medium mt-1">
                            {alert.distance_miles} miles from client ({alert.geofence_radius} mile radius)
                          </p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <span>Caregiver: {alert.gps_lat?.toFixed(4)}, {alert.gps_lng?.toFixed(4)}</span>
                            <span>Client: {alert.client_lat?.toFixed(4)}, {alert.client_lng?.toFixed(4)}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {format(new Date(alert.created_at), 'MMM d, yyyy h:mm a')}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 shrink-0">
                        {!alert.is_resolved ? (
                          <Button size="sm" variant="outline" className="rounded-xl gap-1" onClick={() => handleResolveAlert(alert.id)} data-testid={`resolve-alert-${alert.id}`}>
                            <CheckCircle weight="bold" className="w-3 h-3" /> Resolve
                          </Button>
                        ) : (
                          <Badge className="bg-green-100 text-green-800 rounded-full">Resolved</Badge>
                        )}
                        <a
                          href={`https://www.google.com/maps?q=${alert.gps_lat},${alert.gps_lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline text-center"
                        >
                          View Map
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Supervisor: All visits with GPS data */}
      {isSupervisor && (
        <Card className="rounded-2xl border border-border" data-testid="gps-tracking-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <NavigationArrow weight="duotone" className="w-5 h-5 text-primary" />
              GPS Tracked Visits
            </CardTitle>
            <CardDescription>All visits with location tracking data</CardDescription>
          </CardHeader>
          <CardContent>
            {allVisitsWithGps.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MapPin weight="duotone" className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No GPS-tracked visits yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {allVisitsWithGps.slice(0, 20).map(visit => (
                  <div key={visit.id} className="p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => setViewGps(visit)} data-testid={`gps-visit-${visit.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                          <MapPin weight="duotone" className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{getClientName(visit.client_id)}</p>
                          <p className="text-xs text-muted-foreground">{visit.scheduled_date} at {visit.scheduled_time}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                            {visit.check_in_time && <span>In: {fmtClientTimeOnly(visit.check_in_time, getClientTz(visit.client_id))}</span>}
                            {visit.check_out_time && <span>Out: {fmtClientTimeOnly(visit.check_out_time, getClientTz(visit.client_id))}</span>}
                          </div>
                        </div>
                      </div>
                      <Badge className={`rounded-full text-xs ${visit.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                        {visit.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* GPS Detail Dialog */}
      <Dialog open={!!viewGps} onOpenChange={(open) => { if (!open) setViewGps(null); }}>
        <DialogContent className="sm:max-w-lg">
          {viewGps && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">GPS Location Details</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <User weight="duotone" className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{getClientName(viewGps.client_id)}</p>
                    <p className="text-sm text-muted-foreground">{viewGps.scheduled_date} at {viewGps.scheduled_time}</p>
                  </div>
                </div>

                {/* Check In Location */}
                <Card className="rounded-xl border border-green-200 bg-green-50/30">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                        <Play weight="bold" className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-green-800">Clock In</p>
                        {viewGps.check_in_time && <p className="text-xs text-muted-foreground">{fmtClientTime(viewGps.check_in_time, getClientTz(viewGps.client_id))} {tzAbbrev(viewGps.check_in_time, getClientTz(viewGps.client_id))}</p>}
                        {viewGps.check_in_lat ? (
                          <>
                            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                              <NavigationArrow weight="duotone" className="w-3 h-3" />
                              {viewGps.check_in_lat?.toFixed(6)}, {viewGps.check_in_lng?.toFixed(6)}
                            </p>
                            {viewGps.check_in_address && (
                              <p className="text-xs text-muted-foreground mt-1 flex items-start gap-1">
                                <MapPin weight="duotone" className="w-3 h-3 mt-0.5 shrink-0" />
                                {viewGps.check_in_address}
                              </p>
                            )}
                            <a
                              href={`https://www.google.com/maps?q=${viewGps.check_in_lat},${viewGps.check_in_lng}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary hover:underline mt-1 inline-block"
                            >
                              View on Google Maps
                            </a>
                          </>
                        ) : (
                          <p className="text-xs text-muted-foreground mt-1 italic">No GPS data recorded</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Check Out Location */}
                <Card className="rounded-xl border border-red-200 bg-red-50/30">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
                        <Stop weight="bold" className="w-4 h-4 text-red-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-red-800">Clock Out</p>
                        {viewGps.check_out_time ? (
                          <>
                            <p className="text-xs text-muted-foreground">{fmtClientTime(viewGps.check_out_time, getClientTz(viewGps.client_id))} {tzAbbrev(viewGps.check_out_time, getClientTz(viewGps.client_id))}</p>
                            {viewGps.check_out_lat ? (
                              <>
                                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                  <NavigationArrow weight="duotone" className="w-3 h-3" />
                                  {viewGps.check_out_lat?.toFixed(6)}, {viewGps.check_out_lng?.toFixed(6)}
                                </p>
                                {viewGps.check_out_address && (
                                  <p className="text-xs text-muted-foreground mt-1 flex items-start gap-1">
                                    <MapPin weight="duotone" className="w-3 h-3 mt-0.5 shrink-0" />
                                    {viewGps.check_out_address}
                                  </p>
                                )}
                                <a
                                  href={`https://www.google.com/maps?q=${viewGps.check_out_lat},${viewGps.check_out_lng}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-primary hover:underline mt-1 inline-block"
                                >
                                  View on Google Maps
                                </a>
                              </>
                            ) : (
                              <p className="text-xs text-muted-foreground mt-1 italic">No GPS data recorded</p>
                            )}
                          </>
                        ) : (
                          <p className="text-xs text-muted-foreground mt-1 italic">Not yet clocked out</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Duration */}
                {viewGps.check_in_time && viewGps.check_out_time && (
                  <div className="text-center p-3 rounded-xl bg-muted/30">
                    <p className="text-xs text-muted-foreground">Total Duration</p>
                    <p className="font-heading text-2xl font-semibold">
                      {((new Date(viewGps.check_out_time) - new Date(viewGps.check_in_time)) / 3600000).toFixed(2)} hours
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* No visits today */}
      {!isSupervisor && todayVisits.length === 0 && (
        <Card className="rounded-2xl border border-border">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" />
            <h3 className="font-heading text-lg font-medium">No visits today</h3>
            <p className="text-muted-foreground text-sm mt-1">Check your schedule for upcoming visits</p>
          </CardContent>
        </Card>
      )}

      {/* Visit Completion Dialog — tasks + signatures on clock-out */}
      <VisitCompletionDialog
        open={completionOpen}
        onOpenChange={(o) => { if (!submittingCheckout) { setCompletionOpen(o); if (!o) setCompletionVisit(null); } }}
        visit={completionVisit}
        clientId={completionVisit?.client_id}
        onSubmit={handleSubmitCompletion}
        submitting={submittingCheckout}
      />

      {/* Supervisor force clock-out dialog */}
      <Dialog open={!!forceVisit} onOpenChange={(o) => { if (!o && !forceSaving) setForceVisit(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="force-checkout-dialog">
          {forceVisit && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">Clock Out Caregiver</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="p-3 rounded-xl bg-muted/40 text-sm">
                  <p><span className="text-muted-foreground">Caregiver:</span> <span className="font-medium">{getCaregiverName(forceVisit.caregiver_id)}</span></p>
                  <p><span className="text-muted-foreground">Client:</span> <span className="font-medium">{getClientName(forceVisit.client_id)}</span></p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Clocked in {forceVisit.check_in_time ? `${fmtClientTime(forceVisit.check_in_time, getClientTz(forceVisit.client_id))} ${tzAbbrev(forceVisit.check_in_time, getClientTz(forceVisit.client_id))}` : '—'}
                  </p>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Clock-out time <span className="text-xs text-muted-foreground">({tzAbbrev(undefined, getClientTz(forceVisit.client_id))} — client's local time)</span></label>
                  <input
                    type="datetime-local"
                    value={forceTime}
                    onChange={(e) => setForceTime(e.target.value)}
                    className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                    data-testid="force-checkout-time"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Reason <span className="text-destructive">*</span></label>
                  <textarea
                    value={forceReason}
                    onChange={(e) => setForceReason(e.target.value)}
                    placeholder="e.g. Caregiver forgot to clock out; confirmed with client end time at 6 PM"
                    rows={3}
                    className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm resize-none"
                    data-testid="force-checkout-reason"
                  />
                  <p className="text-xs text-muted-foreground">Recorded in the visit's audit trail.</p>
                </div>
                <div className="flex gap-3 pt-1">
                  <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setForceVisit(null)} disabled={forceSaving}>Cancel</Button>
                  <Button
                    className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 text-white gap-2"
                    onClick={submitForceCheckout}
                    disabled={forceSaving}
                    data-testid="confirm-force-checkout-btn"
                  >
                    {forceSaving ? <Spinner className="w-4 h-4 animate-spin" /> : <Stop weight="bold" className="w-4 h-4" />}
                    {forceSaving ? 'Saving...' : 'Clock Out'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClockPage;
